import React from 'react';
import { Box, Divider, Typography as _Typography, Paper as _Paper } from '@mui/material';
import { FacilitiesTable } from './FacilitiesTable';
import { CaseReviewForm } from './CaseReviewForm';
import TemplateDocuments from '../TemplateDocuments';
import { CaseSummary } from './CaseSummary';
import { Gavel as _GavelIcon } from '@mui/icons-material';

interface ReviewFacilitiesProps {
  caseId?: string;
  caseData?: any;
  scopingData?: any;
  // Facilities props
  facilities: any[];
  facilitiesWithSchemeData: any[];
  facilitiesData: any;
  facilitiesLoading: boolean;
  facilitiesSearch: string;
  facilitiesStateFilter: string;
  facilitiesPage: number;
  facilitiesPageSize: number;
  sortField: string;
  sortDirection: 'asc' | 'desc';
  expandedFacilitiesScheme: Set<string>;
  expandedSchemeInstances: Set<string>;
  highlightedOwner: string | null;
  states: any[];
  ownershipChangesData: any;
  onSearchChange: (value: string) => void;
  onStateFilterChange: (value: string) => void;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  onSort: (field: string) => void;
  onFacilitySchemeExpand: (ccn: string) => void;
  onSchemeInstanceExpand: (instanceKey: string) => void;
  onOwnerPillClick: (ownerName: string) => void;

  // Shared
  owners: any[];
  formatMoney: (amount: number) => string;
  // Demo mode - hides CaseSummary, TemplateDocuments, and CaseReviewForm
  demoMode?: boolean;
}

export const ReviewFacilities: React.FC<ReviewFacilitiesProps> = ({
  caseId,
  caseData,
  scopingData: _scopingData,
  facilities,
  facilitiesWithSchemeData,
  facilitiesData,
  facilitiesLoading,
  facilitiesSearch,
  facilitiesStateFilter,
  facilitiesPage,
  facilitiesPageSize,
  sortField,
  sortDirection,
  expandedFacilitiesScheme,
  expandedSchemeInstances,
  highlightedOwner,
  states,
  ownershipChangesData,
  onSearchChange,
  onStateFilterChange,
  onPageChange,
  onPageSizeChange,
  onSort,
  onFacilitySchemeExpand,
  onSchemeInstanceExpand,
  onOwnerPillClick,
  owners,
  formatMoney,
  demoMode = false
}) => {
  return (
    <Box>
      {/* Case Summary Section - hidden in demo mode (shown above tabs instead) */}
      {!demoMode && caseId && (
        <>
          <CaseSummary caseId={caseId} demoMode={demoMode} />
          <Divider sx={{ my: 4 }} />
        </>
      )}

      {/* Template Documents Section - hidden in demo mode */}
      {!demoMode && caseId && caseData && (
        <>
          <TemplateDocuments
            caseId={caseId}
            caseData={caseData}
          />
          <Divider sx={{ my: 4 }} />
        </>
      )}

      {/* Facilities Table */}
      <FacilitiesTable
        facilities={facilities}
        facilitiesWithSchemeData={facilitiesWithSchemeData}
        facilitiesData={facilitiesData}
        facilitiesLoading={facilitiesLoading}
        facilitiesSearch={facilitiesSearch}
        facilitiesStateFilter={facilitiesStateFilter}
        facilitiesPage={facilitiesPage}
        facilitiesPageSize={facilitiesPageSize}
        sortField={sortField}
        sortDirection={sortDirection}
        expandedFacilitiesScheme={expandedFacilitiesScheme}
        expandedSchemeInstances={expandedSchemeInstances}
        highlightedOwner={highlightedOwner}
        owners={owners}
        states={states}
        ownershipChangesData={ownershipChangesData}
        onSearchChange={onSearchChange}
        onStateFilterChange={onStateFilterChange}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
        onSort={onSort}
        onFacilitySchemeExpand={onFacilitySchemeExpand}
        onSchemeInstanceExpand={onSchemeInstanceExpand}
        onOwnerPillClick={onOwnerPillClick}
        formatMoney={formatMoney}
        showCollapseToggle={false}
        demoMode={demoMode}
      />

      {/* Divider and Case Review Form - hidden in demo mode */}
      {!demoMode && (
        <>
          <Divider sx={{ my: 6, borderBottomWidth: 3, borderColor: 'primary.main' }} />
          {caseId && <CaseReviewForm caseId={caseId} />}
        </>
      )}
    </Box>
  );
};